import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { useClickOutside, useKeyboardShortcuts } from '@fiverr-private/hooks';
import { Fade, SlideFade } from '@fiverr-private/transition';
import { Portal } from '@fiverr-private/ui_utils';
import Overlay from '../../Overlay';
import { BASE_Z_INDEX } from '../../Overlay/constants';
import { useModalContext } from '../ModalContext';
import { ModalContentProps } from '../types';

import styles from './index.module.scss';

const zIndex = BASE_Z_INDEX + 1400;

const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  (
    { children, closeOnClickOutside = true, initialFocusRef, renderWhenClosed = false, size = 'md' }: ModalContentProps,
    ref
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const { close, isOpen, isNestedModal, mobileFullScreen } = useModalContext();

    const articleClassName = classNames(styles.dialog, styles[size], { [styles.mobileFullScreen]: mobileFullScreen });

    useKeyboardShortcuts([
      {
        trigger: 'escape',
        handler: close,
      },
    ]);

    useClickOutside(contentRef, closeOnClickOutside ? close : undefined);

    React.useLayoutEffect(() => {
      if (contentRef.current && isOpen) {
        contentRef.current.focus();
        if (initialFocusRef?.current) {
          initialFocusRef.current.focus();
        }
      }
    }, [initialFocusRef, isOpen, contentRef]);

    const Wrapper = isNestedModal ? React.Fragment : Portal;
    const wrapperProps = isNestedModal ? {} : { containerClassName: styles.modalPortal };

    return (
      <Wrapper {...wrapperProps}>
        <div className={styles.wrapperContent} ref={ref}>
          <SlideFade delay={'fast3'} in={isOpen} unmountOnExit={!renderWhenClosed}>
            <div className={classNames(styles.modalContainer, styles.centered)} style={{ zIndex }}>
              <article aria-modal role="dialog" className={articleClassName}>
                <div ref={contentRef} tabIndex={-1} className={classNames(styles.modalContent)}>
                  {children}
                </div>
              </article>
            </div>
          </SlideFade>
          <Fade delay={{ enter: 'fast1', exit: 'moderate1' }} in={isOpen} unmountOnExit={!renderWhenClosed}>
            <Overlay lockBodyScroll={isOpen} />
          </Fade>
        </div>
      </Wrapper>
    );
  }
);

ModalContent.displayName = 'ModalContent';

export default ModalContent;
